import { useEffect, useRef, useState } from "react";
import moment from "moment";
import Modal from "react-modal";
import toast from "react-hot-toast";
import { AiOutlineClose } from "react-icons/ai";
import { MdDeleteOutline } from "react-icons/md";
import Api from "../Api/Api";
import Listing from "../Api/Listing";
import AdminLayout from "../Layout/AdminLayout";
import Loading from "../components/Loading";
import NoData from "../components/NoData";

const initialCreateState = {
    image: null,
    status: "active",
};

function SideBar() {
    const [sliders, setSliders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [record, setRecord] = useState(initialCreateState);
    const [previewImage, setPreviewImage] = useState("");
    const [deleteState, setDeleteState] = useState({
        open: false,
        id: "",
    });
    const createFileInputRef = useRef(null);

    const revokeObjectUrl = (url) => {
        if (url && url.startsWith("blob:")) {
            URL.revokeObjectURL(url);
        }
    };

    const isValidImageFile = (file) => file && file.type.startsWith("image/");
    const backendBaseUrl = Api.defaults.baseURL || "";
    const resolveImageUrl = (imagePath) => {
        if (!imagePath) {
            return "";
        }

        if (/^(blob:|data:|https?:\/\/)/i.test(imagePath)) {
            return imagePath;
        }

        const normalizedBaseUrl = backendBaseUrl.endsWith("/")
            ? backendBaseUrl.slice(0, -1)
            : backendBaseUrl;
        const normalizedImagePath = imagePath.startsWith("/")
            ? imagePath
            : `/${imagePath}`;

        return `${normalizedBaseUrl}${normalizedImagePath}`;
    };

    const fetchSliders = async () => {
        setLoading(true);
        try {
            const main = new Listing();
            const response = await main.sliderList();
            setSliders(response?.data?.data || []);
        } catch (error) {
            console.error("Error fetching sliders:", error);
            toast.error("Unable to fetch sliders.");
            setSliders([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSliders();
    }, []);

    useEffect(() => {
        return () => {
            revokeObjectUrl(previewImage);
        };
    }, [previewImage]);

    const resetCreateForm = () => {
        revokeObjectUrl(previewImage);
        setRecord(initialCreateState);
        setPreviewImage("");
        if (createFileInputRef.current) {
            createFileInputRef.current.value = "";
        }
    };

    const handleCreateInputs = (e) => {
        const { name, value, files } = e.target;

        if (name === "image") {
            const file = files && files[0] ? files[0] : null;

            if (file && !isValidImageFile(file)) {
                toast.error("Please select a valid image file.");
                if (createFileInputRef.current) {
                    createFileInputRef.current.value = "";
                }
                return;
            }

            revokeObjectUrl(previewImage);
            setRecord((prevState) => ({
                ...prevState,
                image: file,
            }));
            setPreviewImage(file ? URL.createObjectURL(file) : "");
            return;
        }

        setRecord((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleCreate = async (e) => {
        e.preventDefault();

        if (!record.image) {
            toast.error("Please select a slider image.");
            return;
        }

        setSubmitting(true);
        try {
            const main = new Listing();
            const data = new FormData();
            data.append("image", record.image);
            data.append("status", record.status);

            const response = await main.sliderAdd(data);
            if (response?.data?.status) {
                toast.success(response.data.message || "Slider added successfully.");
                resetCreateForm();
                fetchSliders();
            } else {
                toast.error(response?.data?.message || "Failed to add slider.");
            }
        } catch (error) {
            console.error("Error adding slider:", error);
            toast.error("Error adding slider.");
        } finally {
            setSubmitting(false);
        }
    };

    const openDeleteModal = (item) => {
        setDeleteState({
            open: true,
            id: item?._id || "",
        });
    };

    const closeDeleteModal = () => {
        setDeleteState({
            open: false,
            id: "",
        });
    };

    const handleDelete = async () => {
        if (!deleteState.id) {
            return;
        }

        setSubmitting(true);
        try {
            const main = new Listing();
            const response = await main.sliderDelete({
                sliderId: deleteState.id,
                Id: deleteState.id,
                _id: deleteState.id,
                id: deleteState.id,
            });

            if (response?.data?.status) {
                toast.success(response.data.message || "Slider deleted successfully.");
                closeDeleteModal();
                fetchSliders();
            } else {
                toast.error(response?.data?.message || "Failed to delete slider.");
            }
        } catch (error) {
            console.error("Error deleting slider:", error);
            toast.error("Error deleting slider.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AdminLayout title="Slider Management">
            <div className="space-y-6">
                <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                    <div className="xl:col-span-8">
                        <div className="bg-white p-5 lg:p-6 shadow-lg rounded-2xl border border-gray-100 h-full">
                            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-6">
                                <div>
                                    <h4 className="text-xl font-semibold text-gray-800">Add New Slider</h4>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Create homepage banners with image and status.
                                    </p>
                                </div>
                                <div className="hidden xl:flex items-center gap-3 rounded-xl bg-blue-50 px-4 py-3 border border-blue-100">
                                    <div className="h-10 w-10 rounded-full bg-blue-600 text-white flex items-center justify-center text-lg font-semibold">
                                        {sliders.length}
                                    </div>
                                    <div>
                                        <p className="text-xs uppercase tracking-wide text-blue-600 font-semibold">Total Sliders</p>
                                        <p className="text-sm text-gray-600">Visible records in current list</p>
                                    </div>
                                </div>
                            </div>

                            <form onSubmit={handleCreate} className="space-y-5">
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-4">
                                    <div className="form-group xl:col-span-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Status
                                        </label>
                                        <select
                                            className="form-control border border-gray-300 rounded-xl p-3 w-full bg-white"
                                            name="status"
                                            value={record.status}
                                            onChange={handleCreateInputs}
                                        >
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                    </div>

                                    <div className="form-group xl:col-span-8">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Slider Image
                                        </label>
                                        <input
                                            required
                                            className="form-control border border-gray-300 rounded-xl p-3 w-full bg-white"
                                            type="file"
                                            name="image"
                                            accept="image/*"
                                            ref={createFileInputRef}
                                            onChange={handleCreateInputs}
                                        />
                                    </div>

                                    <div className="form-group xl:col-span-4 flex items-end">
                                        <button
                                            type="submit"
                                            className="btn bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 w-full rounded-xl"
                                            disabled={submitting}
                                        >
                                            {submitting ? "Saving..." : "Add Slider"}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>

                    <div className="xl:col-span-4">
                        <div className="bg-white p-5 lg:p-6 shadow-lg rounded-2xl border border-gray-100 h-full">
                            <h4 className="text-lg font-semibold text-gray-800">Preview</h4>
                            <p className="text-sm text-gray-500 mt-1 mb-4">
                                Desktop preview of the image selected for the new slider.
                            </p>

                            {previewImage ? (
                                <div className="space-y-4">
                                    <img
                                        src={previewImage}
                                        alt="Slider preview"
                                        className="h-[260px] w-full object-cover rounded-2xl border border-gray-200"
                                    />
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="rounded-xl bg-gray-50 border border-gray-200 px-4 py-3">
                                            <p className="text-xs uppercase tracking-wide text-gray-500">Status</p>
                                            <p className="mt-1 font-semibold capitalize text-gray-800">{record.status || "-"}</p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="h-[260px] rounded-2xl border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-center px-6">
                                    <div>
                                        <p className="text-base font-semibold text-gray-700">No image selected</p>
                                        <p className="text-sm text-gray-500 mt-1">
                                            Upload an image to see how the slider card will look on desktop.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="bg-white shadow-lg rounded-2xl p-5 lg:p-6 border border-gray-100">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-5">
                        <div>
                            <h4 className="text-xl font-semibold text-gray-800">Slider List</h4>
                            <p className="text-sm text-gray-500 mt-1">
                                Manage existing banners and remove sliders you no longer need.
                            </p>
                        </div>
                        <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600 w-fit">
                            <span className="h-2.5 w-2.5 rounded-full bg-blue-600"></span>
                            Total sliders: {sliders.length}
                        </div>
                    </div>

                    {loading ? (
                        <Loading message="Fetching sliders..." />
                    ) : sliders.length === 0 ? (
                        <NoData message="No sliders found." />
                    ) : (
                        <div className="overflow-x-auto w-full">
                            <table className="min-w-full bg-white">
                                <thead>
                                    <tr className="bg-gray-100 text-gray-600 uppercase text-xs tracking-wide">
                                        <th className="py-4 px-4 text-left font-semibold">#</th>
                                        <th className="py-4 px-4 text-left font-semibold">Image</th>
                                        <th className="py-4 px-4 text-left font-semibold">Status</th>
                                        <th className="py-4 px-4 text-left font-semibold">Created At</th>
                                        <th className="py-4 px-4 text-left font-semibold">Delete</th>
                                    </tr>
                                </thead>
                                <tbody className="text-gray-600 text-sm">
                                    {sliders.map((item, index) => (
                                        <tr
                                            key={item?._id || index}
                                            className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                                        >
                                            <td className="py-4 px-4 text-left font-medium">{index + 1}</td>
                                            <td className="py-4 px-4 text-left">
                                                <img
                                                    src={resolveImageUrl(item?.image)}
                                                    alt="Slider"
                                                    className="h-20 w-32 object-cover rounded-xl border border-gray-200 shadow-sm"
                                                />
                                            </td>
                                            <td className="py-4 px-4 text-left">
                                                <span
                                                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold capitalize ${item?.status === "active"
                                                        ? "bg-green-100 text-green-700"
                                                        : "bg-red-100 text-red-600"
                                                        }`}
                                                >
                                                    {item?.status}
                                                </span>
                                            </td>
                                            <td className="py-4 px-4 text-left whitespace-nowrap">
                                                {item?.created_at ? moment(item.created_at).format("DD MMM YYYY hh:mm A") : "-"}
                                            </td>
                                            <td className="py-4 px-4 text-left">
                                                <button
                                                    type="button"
                                                    className="bg-red-500 text-white px-3 py-2 rounded-xl hover:bg-red-600 transition-colors"
                                                    onClick={() => openDeleteModal(item)}
                                                >
                                                    <MdDeleteOutline size={20} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {deleteState.open && (
                <Modal
                    isOpen={deleteState.open}
                    onRequestClose={closeDeleteModal}
                    aria-labelledby="slider-delete-title"
                    aria-describedby="slider-delete-description"
                    className="max-w-md mx-auto"
                    overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center px-4"
                >
                    <div className="relative bg-white rounded-lg shadow-lg w-full">
                        <div className="flex justify-between items-center border-b border-gray-200 py-4 px-6">
                            <h2 id="slider-delete-title" className="text-xl font-semibold text-gray-800">
                                Delete Slider
                            </h2>
                            <button
                                type="button"
                                onClick={closeDeleteModal}
                                className="text-gray-700 hover:text-gray-900"
                            >
                                <AiOutlineClose className="h-6 w-6" />
                            </button>
                        </div>

                        <div className="py-6 px-6">
                            <p id="slider-delete-description" className="text-gray-600">
                                Are you sure you want to delete{" "}
                                <span className="font-semibold text-gray-800">
                                    this slider
                                </span>
                                ?
                            </p>

                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={closeDeleteModal}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleDelete}
                                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700"
                                    disabled={submitting}
                                >
                                    {submitting ? "Deleting..." : "Delete"}
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}
        </AdminLayout>
    );
}

export default SideBar;